/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Login from '../app/auth/Login';
import setAuthToken from '../axiosConfig';
import DefaultLayout from '../components/layout/DefaultLayout';
import ErrorPage from '../components/common/ErrorPage';
import { useSelector } from 'react-redux';
import UseScrollToTop from '../components/common/UseScrollToTop';
import NoDataFound from '../components/common/NoDataFound';
import lazyWithRetry from '../utills/lazyWithRetry';

// Screens load on demand (code splitting); shell components above stay eager.
const ForgotPasswordPage = lazyWithRetry(() => import('../app/auth/ForgotPasswordPage'));
const AdminDashboard = lazyWithRetry(() => import('../app/admin/dashboard/AdminDashboard'));
const Users = lazyWithRetry(() => import('../app/admin/users/Users'));
const Permissions = lazyWithRetry(() => import('../app/admin/permissions/Permissions'));
const Leads = lazyWithRetry(() => import('../app/admin/leads/Leads'));
const ModelMaster = lazyWithRetry(() => import('../../src/app/admin/masters/Model'));
const ExchangeModelMaster = lazyWithRetry(() => import('../app/admin/masters/ExchangeModel'));
const TerritoryMaster = lazyWithRetry(() => import('../app/admin/masters/Territory'));
const TransmissionMaster = lazyWithRetry(() => import('../app/admin/masters/Transmission'));
const VariantMaster = lazyWithRetry(() => import('../app/admin/masters/Variant'));
const VehicleMaster = lazyWithRetry(() => import('../app/admin/masters/Vehicle'));
const AreaMaster = lazyWithRetry(() => import('../app/admin/masters/Area'));
const BankMaster = lazyWithRetry(() => import('../app/admin/masters/Bank'));
const AddVehicle = lazyWithRetry(() => import('../app/admin/masters/AddVehicle'));
const AddExchangeModel = lazyWithRetry(() => import('../app/admin/masters/AddExchangeModel'));
const AddModel = lazyWithRetry(() => import('../app/admin/masters/AddModel'));
const AddArea = lazyWithRetry(() => import('../app/admin/masters/AddArea'));
const AddVariant = lazyWithRetry(() => import('../app/admin/masters/AddVariant'));
const AddTerritory = lazyWithRetry(() => import('../app/admin/masters/AddTerritory'));
const AddTransmission = lazyWithRetry(() => import('../app/admin/masters/AddTransmission'));
const AddBank = lazyWithRetry(() => import('../app/admin/masters/AddBank'));
const Reports = lazyWithRetry(() => import('../app/admin/reports/Reports'));
const UserDetails = lazyWithRetry(() => import('../app/admin/users/UserDetails'));
const AddUsers = lazyWithRetry(() => import('../app/admin/users/AddUsers'));
const AddTarget = lazyWithRetry(() => import('../app/admin/users/AddTarget'));
const AddLeads = lazyWithRetry(() => import('../app/admin/leads/AddLeads'));
const ExchangeEvaluation = lazyWithRetry(() => import('../app/admin/leads/Exchange/ExchangeEvaluation'));
const ViewLeads = lazyWithRetry(() => import('../app/admin/leads/ViewLeads'));
const LeadDetails = lazyWithRetry(() => import('../app/admin/leads/LeadDetails'));
const CallLogs = lazyWithRetry(() => import('../app/admin/leads/CallLogs'));
const HomeVisit = lazyWithRetry(() => import('../app/admin/leads/HomeVisit'));
const ExchangeDetails = lazyWithRetry(() => import('../app/admin/leads/Exchange/ExchangeDetails'));
const Profile = lazyWithRetry(() => import('../components/profile/CompanyProfileEdit'));
const SalesDashboard = lazyWithRetry(() => import('../app/sales-team/dashboard/SalesDashboard'));
const SalesModalMaster = lazyWithRetry(() => import('../app/sales-team/modal-master/SalesModalMaster'));
const ViewExchange = lazyWithRetry(() => import('../app/exchange-evaluation-team/leads/ViewExchange'));
const AddQuotation = lazyWithRetry(() => import('../app/exchange-evaluation-team/leads/AddQuotation'));
const Retail = lazyWithRetry(() => import('../app/exchange-evaluation-team/leads/Retail'));
const ActivityLog = lazyWithRetry(() => import('../app/exchange-evaluation-team/leads/ActivityLog'));
const Bookings = lazyWithRetry(() => import('../app/exchange-evaluation-team/leads/Booking/Bookings'));
const BookingDetails = lazyWithRetry(() => import('../app/exchange-evaluation-team/leads/Booking/BookingDetails'));
const MyLeads = lazyWithRetry(() => import('../app/sales-team/leads/MyLeads'));
const AdminProfile = lazyWithRetry(() => import('../components/profile/CompanyProfile'));
const ProfileDetailsEdit = lazyWithRetry(() => import('../components/profile/ProfileDetailsEdit'));
const DashboardHome = lazyWithRetry(() => import('../app/admin/dashboard/components/DashboardHome'));
const DashboardTarget = lazyWithRetry(() => import('../app/admin/dashboard/components/DashboardTarget'));
const ResetPassword = lazyWithRetry(() => import('../app/auth/ResetPassword'));
const ChangePassword = lazyWithRetry(() => import('../app/auth/ChangePassword'));
const ExchangeLeads = lazyWithRetry(() => import('../app/admin/leads/Exchange/ExchangeLeads'));
const ViewQuotation = lazyWithRetry(() => import('../app/exchange-evaluation-team/leads/ViewQuotation'));
const ExchangeDetailsWithPRocured = lazyWithRetry(() => import('../app/admin/leads/Exchange/ExchangeDetailsWithPRocured'));
const ExchangeEvaluationMultiStep = lazyWithRetry(() => import('../app/admin/leads/Exchange/ExchangeEvaluationMultiStep'));
const ProfileTargets = lazyWithRetry(() => import('../components/profile/ProfileTargets'));
const ReportsComponent = lazyWithRetry(() => import('../app/admin/reports/ReportsComponent'));
const DashboardUsers = lazyWithRetry(() => import('../app/admin/dashboard/components/DashboardUsers'));
const TargetMasters = lazyWithRetry(() => import('../app/admin/masters/TargetMaster'));
const StockList = lazyWithRetry(() => import('../app/admin/stock/StockList'));
const UsedCarsList = lazyWithRetry(() => import('../app/admin/stock/UsedCarsList'));
const BulkUpdateHistory = lazyWithRetry(() => import('../app/admin/stock/BulkUpdateHistory'));
const AddEditVinForm = lazyWithRetry(() => import('../app/admin/stock/AddEditVinForm'));
const StockDetails = lazyWithRetry(() => import('../app/admin/stock/StockDetails'));
const AllocationRequests = lazyWithRetry(() => import('../app/admin/requests/AllocationRequests'));
const DeliveryRequests = lazyWithRetry(() => import('../app/admin/requests/DeliveryRequests'));
const CancellationRequests = lazyWithRetry(() => import('../app/admin/requests/CancellationRequests'));
const PendingRequests = lazyWithRetry(() => import('../app/admin/requests/PendingRequests'));

function Routers() {
  const isAuthenticated = localStorage?.getItem('authToken');
  const roleType = useSelector((state) => state?.auth?.userData?.role);
  const isAdmin = useSelector((state) => state?.auth?.isAdmin);
  const permissions = useSelector((state) => state?.auth?.userData?.permissions);

  useEffect(() => {
    setAuthToken();
  }, [isAuthenticated]);

  UseScrollToTop();

  const adminRoutes = () => {
    return (
      <>
        <Route path='/' element={<DefaultLayout />}>
          <Route index element={<Navigate to='/admin/dashboard' replace />} />
          <Route path='/admin' element={<AdminDashboard />} />
          <Route path='/admin/dashboard' element={<AdminDashboard />} />
          <Route path='/admin/dashboard/home' element={<DashboardHome />} />
          <Route path='/admin/dashboard/target' element={<DashboardTarget />} />
          <Route path='/admin/users' element={<Users />} />
          <Route path='/admin/users/details/:id' element={<UserDetails />} />
          <Route path='/admin/users/edit/:id' element={<UserDetails />} />
          <Route path='/admin/users/add' element={<AddUsers />} />
          <Route path='/admin/add-target' element={<AddTarget />} />
          <Route path='/admin/edit-target/:id/:monthID' element={<AddTarget />} />
          <Route path='/admin/profile' element={<AdminProfile />} />
          <Route path='/admin/profile/edit' element={<Profile />} />
          <Route path='/admin/target/:id' element={<DashboardUsers />} />
          <Route path='/admin/dashboard-user/:id' element={<DashboardUsers />} />
          <Route path='/admin/profile/details-edit' element={<ProfileDetailsEdit />} />
          <Route path='/admin/permissions' element={<Permissions />} />
          <Route path='/admin/leads' element={<Leads />} />
          {/* Additional routes for /leads and /profile paths */}
          <Route path='/leads' element={<Leads />} />
          <Route path='/leads/edit/:id' element={<AddLeads />} />
          <Route path='/leads/my-leads' element={<MyLeads />} />
          <Route path='/leads/lead-details' element={<LeadDetails />} />
          <Route path='/leads/view/:id' element={<ViewLeads />} />
          <Route path='/profile' element={<AdminProfile />} />
          <Route path='/profile/edit' element={<ProfileDetailsEdit />} />
          <Route path='/change-password' element={<ChangePassword />} />
          <Route path='/admin/leads/add' element={<AddLeads />} />
          <Route path='/admin/leads/edit/:id' element={<AddLeads />} />
          <Route path='/admin/leads/my-leads' element={<MyLeads />} />
          <Route path='/admin/leads/lead-details' element={<LeadDetails />} />
          <Route path='/admin/leads/view/:id' element={<ViewLeads />} />
          <Route path='/admin/leads/call-logs' element={<CallLogs />} />
          <Route path='/admin/leads/home-visit' element={<HomeVisit />} />
          <Route path='/admin/leads/exchange-details' element={<ExchangeDetails />} />
          <Route path='/admin/leads/exchange-lead' element={<ExchangeLeads />} />
          <Route path='/admin/add-quotation' element={<AddQuotation />} />
          <Route path='/admin/view-quotation/:id' element={<ViewQuotation />} />
          <Route path='/admin/retail' element={<Retail />} />
          <Route path='/admin/view-exchange' element={<ViewExchange />} />
          <Route path='/admin/activity-log' element={<ActivityLog />} />
          <Route path='/admin/bookings/:id' element={<Bookings />} />
          <Route path='/admin/booking-details/:id' element={<BookingDetails />} />
          <Route path='/admin/targets/:id' element={<ProfileTargets />} />
          <Route path='/admin/masters/model' element={<ModelMaster />} />
          <Route path='/admin/masters/area' element={<AreaMaster />} />
          <Route path='/admin/masters/exchange-model' element={<ExchangeModelMaster />} />
          <Route path='/admin/masters/territory' element={<TerritoryMaster />} />
          <Route path='/admin/masters/transmission' element={<TransmissionMaster />} />
          <Route path='/admin/masters/variant' element={<VariantMaster />} />
          <Route path='/admin/masters/vehicle' element={<VehicleMaster />} />
          <Route path='/admin/masters/bank' element={<BankMaster />} />
          <Route path='/admin/masters/vehicle/add' element={<AddVehicle />} />
          <Route path='/admin/masters/vehicle/edit/:id' element={<AddVehicle />} />
          <Route path='/admin/masters/exchange-model/add' element={<AddExchangeModel />} />
          <Route path='/admin/masters/exchange-model/edit/:id' element={<AddExchangeModel />} />
          <Route path='/admin/masters/model/add' element={<AddModel />} />
          <Route path='/admin/masters/model/edit/:id' element={<AddModel />} />
          <Route path='/admin/masters/variant/add' element={<AddVariant />} />
          <Route path='/admin/masters/variant/edit/:id' element={<AddVariant />} />
          <Route path='/admin/masters/transmission/add' element={<AddTransmission />} />
          <Route path='/admin/masters/transmission/edit/:id' element={<AddTransmission />} />
          <Route path='/admin/masters/territory/add' element={<AddTerritory />} />
          <Route path='/admin/masters/territory/edit/:id' element={<AddTerritory />} />
          <Route path='/admin/masters/area/add' element={<AddArea />} />
          <Route path='/admin/masters/area/edit/:id' element={<AddArea />} />
          <Route path='/admin/masters/bank/add' element={<AddBank />} />
          <Route path='/admin/masters/bank/edit/:id' element={<AddBank />} />
          <Route path='/admin/masters/target' element={<TargetMasters />} />
          {/* Phase 2 - UI only, dummy data */}
          {/* Stock routes with permission check */}
          <Route 
            path='/admin/stock' 
            element={isAdmin || permissions?.includes('view-new-vehicle-stock') ? <StockList /> : <ErrorPage />} 
          />
          <Route 
            path='/admin/stock/used-cars' 
            element={isAdmin || permissions?.includes('view-new-vehicle-stock') ? <UsedCarsList /> : <ErrorPage />} 
          />
          <Route 
            path='/admin/stock/history' 
            element={isAdmin || permissions?.includes('view-new-vehicle-stock') ? <BulkUpdateHistory /> : <ErrorPage />} 
          />
          <Route 
            path='/admin/stock/add-vin' 
            element={isAdmin || permissions?.includes('add-new-vehicle-stock') ? <AddEditVinForm /> : <ErrorPage />} 
          />
          <Route 
            path='/admin/stock/edit-vin/:id/:vin' 
            element={isAdmin || permissions?.includes('edit-new-vehicle-stock') ? <AddEditVinForm /> : <ErrorPage />} 
          />
          <Route 
            path='/admin/stock/view-vin/:id' 
            element={isAdmin || permissions?.includes('view-new-vehicle-stock') ? <StockDetails /> : <ErrorPage />} 
          />
          {/* Request routes with permission check */}
          <Route 
            path='/admin/requests/allocation' 
            element={isAdmin || permissions?.includes('view-request') ? <AllocationRequests /> : <ErrorPage />} 
          />
          <Route 
            path='/admin/requests/delivery' 
            element={isAdmin || permissions?.includes('view-request') ? <DeliveryRequests /> : <ErrorPage />} 
          />
          <Route 
            path='/admin/requests/cancellation' 
            element={isAdmin || permissions?.includes('view-request') ? <CancellationRequests /> : <ErrorPage />} 
          />
          <Route 
            path='/admin/requests/pending' 
            element={
              (isAdmin || permissions?.includes('view-request')) && 
              roleType !== 'Exchange Evaluation Team' && 
              roleType !== 'Procurement Manager' 
                ? <PendingRequests /> 
                : <ErrorPage /> 
            } 
          />
          <Route path='/admin/reports' element={<Reports />} />
          <Route path='/admin/reports/over-all-reports' element={<ReportsComponent />} />
          <Route path='/admin/reports/booking-reports' element={<ReportsComponent />} />
          <Route path='/admin/reports/exchange-reports' element={<ReportsComponent />} />
          <Route path='/admin/reports/retail-reports' element={<ReportsComponent />} />
          <Route path='/admin/reports/home-visit-test-drive-reports' element={<ReportsComponent />} />
          <Route path='/admin/change-password' element={<ChangePassword />} />
          <Route path='*' element={<ErrorPage />} />
        </Route>
      </>
    );
  };

  const userRoutes = () => {
    return (
      <Route path='/' element={<DefaultLayout />}>
        <Route index element={<Navigate to='/dashboard' replace />} />
        <Route path='/users' element={<Users />} />
        <Route
          path='/users/details/:id'
          element={permissions?.includes('view-user') ? <UserDetails /> : <ErrorPage />}
        />
        <Route path='/users/edit/:id' element={permissions?.includes('edit-user') ? <UserDetails /> : <ErrorPage />} />
        <Route path='/users/add' element={permissions?.includes('add-user') ? <AddUsers /> : <ErrorPage />} />
        <Route path='/profile' element={<AdminProfile />} />
        <Route path='/profile/edit' element={<ProfileDetailsEdit />} />
        <Route path='/dashboard' element={<SalesDashboard />} />
        <Route path='/add-quotation' element={<AddQuotation />} />
        <Route path='/leads' element={<Leads />} />
        <Route path='/leads/edit/:id' element={<AddLeads />} />
        <Route path='/leads/exchange-lead' element={<ExchangeLeads />} />
        <Route path='/target/:id' element={<DashboardUsers />} />
        <Route path='/dashboard-user/:id' element={<DashboardUsers />} />
        <Route path='/exchange-evaluation/:id' element={<ExchangeEvaluation />} />
        <Route path='/exchange-evaluation-details/:id' element={<ExchangeDetailsWithPRocured />} />
        <Route path='/step1vehicleinspection/:id' element={<ExchangeEvaluationMultiStep />} />
        <Route path='/leads/add' element={permissions?.includes('add-lead') ? <AddLeads /> : <ErrorPage />} />
        <Route path='/add-target' element={permissions?.includes('add-target') ? <AddTarget /> : <ErrorPage />} />
        <Route path='/targets/:id' element={<ProfileTargets />} />
        <Route path='/bookings/:id' element={<Bookings />} />
        <Route path='/view-quotation/:id' element={<ViewQuotation />} />
        <Route path='/leads/my-leads' element={<MyLeads />} />
        <Route path='/leads/lead-details' element={<LeadDetails />} />
        <Route path='/add-target' element={<AddTarget />} />
        <Route path='/edit-target/:id' element={<AddTarget />} />
        <Route path='/edit-target/:id/:monthID' element={<AddTarget />} />
        <Route
          path='/leads/view/:id'
          element={
            (
              roleType === 'Exchange Evaluation Team' ||
              roleType === 'Procurement Manager' ||
              [
                'Super Admin',
                'CEO',
                'VP Sales',
                'GM (General Manager)',
                'AGM (Assistant General Manager)',
                'TSM (Territory Sales Manager)',
                'Sales Manager',
                'Team Leader',
                'Sales Officer',
              ].includes(roleType) ||
              permissions?.includes('view-lead')
            ) ? (
              <ViewLeads />
            ) : (
              <ErrorPage />
            )
          }
        />
        <Route path='/booking-details/:id' element={<BookingDetails />} />
        <Route path='/change-password' element={<ChangePassword />} />
        <Route path='/model-master' element={<SalesModalMaster />} />
        {/* Stock routes with permission check */}
        <Route 
          path='/admin/stock' 
          element={permissions?.includes('view-new-vehicle-stock') ? <StockList /> : <ErrorPage />} 
        />
        <Route 
          path='/admin/stock/used-cars' 
          element={permissions?.includes('view-new-vehicle-stock') ? <UsedCarsList /> : <ErrorPage />} 
        />
        <Route 
          path='/admin/stock/history' 
          element={permissions?.includes('view-new-vehicle-stock') ? <BulkUpdateHistory /> : <ErrorPage />} 
        />
        <Route 
          path='/admin/stock/add-vin' 
          element={permissions?.includes('add-new-vehicle-stock') ? <AddEditVinForm /> : <ErrorPage />} 
        />
        <Route 
          path='/admin/stock/edit-vin/:id/:vin' 
          element={permissions?.includes('edit-new-vehicle-stock') ? <AddEditVinForm /> : <ErrorPage />} 
        />
        <Route 
          path='/admin/stock/view-vin/:id' 
          element={permissions?.includes('view-new-vehicle-stock') ? <StockDetails /> : <ErrorPage />} 
        />
        {/* Request routes with permission check */}
        <Route 
          path='/admin/requests/allocation' 
          element={permissions?.includes('view-request') ? <AllocationRequests /> : <ErrorPage />} 
        />
        <Route 
          path='/admin/requests/delivery' 
          element={permissions?.includes('view-request') ? <DeliveryRequests /> : <ErrorPage />} 
        />
        <Route 
          path='/admin/requests/cancellation' 
          element={permissions?.includes('view-request') ? <CancellationRequests /> : <ErrorPage />} 
        />
        <Route 
          path='/admin/requests/pending' 
          element={
            permissions?.includes('view-request') && 
            roleType !== 'Exchange Evaluation Team' && 
            roleType !== 'Procurement Manager' 
              ? <PendingRequests /> 
              : <ErrorPage /> 
          } 
        />
        <Route path='*' element={<ErrorPage />} />
        <Route path='/no-result-found' element={<NoDataFound />} />
      </Route>
    );
  };

  return (
    <Routes>
      {isAuthenticated ? (
        <>{isAdmin ? adminRoutes() : userRoutes()}</>
      ) : (
        <>
          <Route path='/' element={<Login />} />
          <Route path='/admin' element={<Login />} />
          <Route path='/forgot-password' element={<ForgotPasswordPage />} />
          <Route path='/reset-password' element={<ResetPassword />} />
          <Route path='*' element={<Navigate to={isAdmin ? '/admin' : '/'} replace />} />
        </>
      )}
    </Routes>
  );
}

export default Routers;
